import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { Check, Loader2 } from 'lucide-react';
import { BookingError, suggestTime } from '../../services/interviewBookingApi';
import { InlineError } from './BookingPanels';
import { dayKey, TZ } from './format';

const MAX_NOTE_LENGTH = 500;
const MAX_DAYS_AHEAD = 60;

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

/**
 * "None of these work? Suggest a time" — under the free times, only on links that switched it on.
 *
 * The candidate may name ANY day and time range, free or not in the interviewers' calendars; nothing
 * is booked. The recruiter is told and answers by email, so the confirmation says exactly that.
 */
export function SuggestTime({
  token,
  minutes,
  onEvent,
}: {
  token: string;
  /** The interview's length — the range has to hold at least this much. */
  minutes: number;
  /** Funnel events only; never the day, the note or anything typed. */
  onEvent: (name: string, props?: Record<string, string | number | boolean>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [date, setDate] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  // Berlin dates, like every other time on this page; fixed when the form first renders.
  const [{ min, max }] = useState(() => ({
    min: dayKey(new Date().toISOString()),
    max: dayKey(new Date(Date.now() + MAX_DAYS_AHEAD * 86_400_000).toISOString()),
  }));

  if (sent) {
    return (
      <div className="mt-5 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
        <Check className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Thanks — we passed your suggestion on to your recruiter. Nothing is booked yet; they will get
          back to you by email.
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="mt-5 border-t border-gray-100 pt-4 text-sm text-gray-600">
        None of these work?{' '}
        <button
          type="button"
          onClick={() => {
            onEvent('booking_suggest_started');
            setOpen(true);
          }}
          className="font-medium text-gray-900 underline underline-offset-2"
        >
          Suggest a time
        </button>
      </div>
    );
  }

  const rangeLongEnough = from !== '' && to !== '' && toMinutes(to) - toMinutes(from) >= minutes;
  const canSubmit = date !== '' && rangeLongEnough && note.trim().length <= MAX_NOTE_LENGTH;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await suggestTime(token, { date, from, to, note: note.trim() });
      onEvent('booking_time_suggested', { has_note: note.trim().length > 0 });
      setSent(true);
    } catch (err) {
      onEvent('booking_time_suggest_failed', { status: err instanceof BookingError ? err.status : 0 });
      setError(err instanceof Error ? err.message : 'Could not send your suggestion');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mt-5 border-t border-gray-100 pt-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit && !busy) void submit();
      }}
    >
      <h3 className="text-sm font-semibold text-gray-900">Suggest a time</h3>
      <p className="mt-1 text-sm text-gray-600">
        Tell us a day and the hours that suit you ({TZ} time). Nothing is booked — your recruiter will
        reply by email.
      </p>

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-700">Day</span>
          <Input
            type="date"
            value={date}
            min={min}
            max={max}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-700">From</span>
          <Input type="time" value={from} onChange={(e) => setFrom(e.target.value)} required />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-700">Until</span>
          <Input type="time" value={to} onChange={(e) => setTo(e.target.value)} required />
        </label>
      </div>
      {from !== '' && to !== '' && !rangeLongEnough ? (
        <p className="mt-1 text-xs text-red-600">
          The interview takes {minutes} minutes, so the time range needs to be at least that long.
        </p>
      ) : null}

      <label className="mt-3 block">
        <span className="mb-1 block text-xs font-medium text-gray-700">Anything to add? (optional)</span>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          maxLength={MAX_NOTE_LENGTH}
          placeholder="For example: I am at work until 17:00"
        />
        <span className="mt-1 block text-xs text-gray-500">This goes to your recruiter.</span>
      </label>

      {error ? <InlineError message={error} /> : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={!canSubmit || busy}>
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
          {busy ? 'Sending…' : 'Send my suggestion'}
        </Button>
        <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
